import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const AuthContext = createContext();

// Token storage helpers
// Token artık HttpOnly cookie'de saklanıyor - JavaScript ile erişilemez
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const SESSION_TOKEN_KEY = 'auth_session_token';

// Eski localStorage token'ını temizle (HttpOnly cookie'ye geçiş)
const clearLegacyToken = () => localStorage.removeItem(TOKEN_KEY);

const getStoredSessionToken = () => {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

const setStoredSessionToken = (token) => {
  try {
    if (token) sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    else sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

const getStoredUser = () => {
  try {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};
const setStoredUser = (user) => localStorage.setItem(USER_KEY, JSON.stringify(user));
const removeStoredUser = () => localStorage.removeItem(USER_KEY);

// Axios: Same-origin cookie otomatik gönderilir, withCredentials gereksiz
axios.interceptors.request.use((config) => {
  return config;
});

// Token interceptor - sessionToken varsa Bearer olarak gönder
let _sessionToken = null;
export const setAxiosToken = (token) => { _sessionToken = token; };
export const clearAxiosToken = () => { _sessionToken = null; };

axios.interceptors.request.use((config) => {
  if (_sessionToken) {
    config.headers['Authorization'] = `Bearer ${_sessionToken}`;
  }
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getStoredUser());
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getStoredUser());
  const [isLoading, setIsLoading] = useState(() => !getStoredUser());
  // Token sadece memory'de - sayfa yenilenince kaybolur, HttpOnly cookie devralır
  const [sessionToken, setSessionToken] = useState(null);

  const apiUrl = API_BASE_URL;

  // Check authentication status on mount
  useEffect(() => {
    const savedToken = getStoredSessionToken();
    if (savedToken) {
      setAxiosToken(savedToken);
      setSessionToken(savedToken);
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedUser = getStoredUser();
    if (!storedUser) {
      setIsLoading(false);
      return;
    }
    
    try {
      // HttpOnly cookie ile /me endpoint'ine istek at
      const response = await axios.get(`${apiUrl}/api/auth/me`);
      
      if (response.data) {
        setUser(response.data);
        setStoredUser(response.data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      // Cookie/session geçersiz - temizle
      clearAxiosToken();
      setStoredSessionToken(null);
      removeStoredUser();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      const response = await axios.post(`${apiUrl}/api/auth/login`, {
        email,
        password
      });
      console.log('Step 2: Response received:', response.data);

      if (response.data.success) {
        const { user: userData, token } = response.data;
        
        // Token sadece memory'de sakla (XSS ile çalınamaz)
        clearLegacyToken();
        if (token) {
          setSessionToken(token);
          setAxiosToken(token);
          setStoredSessionToken(token);
        }
        setStoredUser(userData);
        
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true, user: userData };
      }
      return { success: false, error: 'Login failed' };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Giriş başarısız. Lütfen tekrar deneyin.' 
      };
    }
  };

  const login = async (sessionId) => {
    try {
      const response = await axios.post(
        `${apiUrl}/api/auth/oauth/google/callback`,
        { session_id: sessionId }
      );

      if (response.data.success) {
        const { user: userData, token } = response.data;
        
        clearLegacyToken();
        if (token) {
          setSessionToken(token);
          setAxiosToken(token);
          setStoredSessionToken(token);
        }
        setStoredUser(userData);
        
        setUser(userData);
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = async (opts = {}) => {
    const redirectTo = opts.redirectTo || '/';
    try {
      await axios.post(`${apiUrl}/api/auth/logout`, {});
    } catch (error) {
      // Cookie sunucu tarafında temizlenir
    } finally {
      clearLegacyToken();
      clearAxiosToken();
      setStoredSessionToken(null);
      setSessionToken(null);
      removeStoredUser();
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = redirectTo;
    }
  };

  const refreshUser = async () => {
    const response = await axios.get(`${apiUrl}/api/auth/me`, { withCredentials: true });
    if (response.data) {
      setUser(response.data);
      setStoredUser(response.data);
      setIsAuthenticated(true);
    }
    return response.data;
  };

  const listImpersonationTargets = async () => {
    const res = await axios.get(`${apiUrl}/api/auth/impersonate/targets`, {
      withCredentials: true,
    });
    return res.data?.items || [];
  };

  const applySessionToken = (token) => {
    if (!token) return;
    setSessionToken(token);
    setAxiosToken(token);
    setStoredSessionToken(token);
  };

  const startImpersonation = async (userId) => {
    const res = await axios.post(
      `${apiUrl}/api/auth/impersonate/${encodeURIComponent(userId)}`,
      {},
      { withCredentials: true }
    );
    // New rotated session — must apply before any /me call; cookie may still
    // lag, so Bearer (sessionStorage) is the source of truth.
    applySessionToken(res.data?.token);
    const me = res.data?.me || res.data?.user;
    if (me?.id) {
      setUser(me);
      setStoredUser(me);
      setIsAuthenticated(true);
      return me;
    }
    removeStoredUser();
    return refreshUser();
  };

  const stopImpersonation = async () => {
    const res = await axios.post(
      `${apiUrl}/api/auth/impersonate/stop`,
      {},
      { withCredentials: true }
    );
    applySessionToken(res.data?.token);
    const me = res.data?.me || res.data?.user;
    if (me?.id) {
      setUser(me);
      setStoredUser(me);
      setIsAuthenticated(true);
      return me;
    }
    removeStoredUser();
    return refreshUser();
  };

  const isImpersonating = !!(user?.impersonation?.active);
  const canImpersonate =
    (user?.role === 'admin' && !isImpersonating) || isImpersonating;

  const isAdmin = () => {
    if (!user) return false;

    // Impersonation: never treat as full admin even if target role is somehow admin
    // Panel entry still allowed via can_access_admin_panel / role_ids below.

    // Admin role her zaman tam yetki (yalnızca gerçek admin oturumu)
    if (user.role === 'admin' && !isImpersonating) return true;
    
    // Eğer role_permissions varsa can_access_admin_panel kontrolü yap
    if (user.role_permissions) {
      return user.role_permissions.can_access_admin_panel === true;
    }

    // RBAC role_ids atanmış staff (bootstrap sonrası /me öncesi)
    if (Array.isArray(user.role_ids) && user.role_ids.length > 0) {
      return true;
    }
    
    return false;
  };

  // Granüler menü izin kontrolü
  const hasMenuPermission = (menuId, permType = 'can_access') => {
    if (!user) return false;
    // Gerçek admin (impersonate değil) her zaman tam yetki
    if (user.role === 'admin' && !user?.impersonation?.active) return true;
    
    const menuPerms = user.role_permissions?.menu_permissions || [];
    const found = menuPerms.find(mp => mp.menu_id === menuId);
    return found ? found[permType] === true : false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isAdmin,
        hasMenuPermission,
        login,
        loginWithEmail,
        logout,
        checkAuth,
        refreshUser,
        listImpersonationTargets,
        startImpersonation,
        stopImpersonation,
        isImpersonating,
        canImpersonate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
